import React, { useState, useEffect, useRef } from 'react';
import { RouteComponentProps } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useBluetooth } from '../../contexts/BluetoothContext';
import GlobalBluetoothWrapper from '../../utils/GlobalBluetoothWrapper';
import BluetoothSetupFlowWithContext from '../../components/BluetoothSetupFlow/BluetoothSetupFlowWithContext';
import Exercise from './Exercise';
import './ExerciseWithGlobalBluetooth.css';

interface ExerciseWithGlobalBluetoothProps extends RouteComponentProps, WithTranslation {
  // Pass through all the props that Exercise needs
  [key: string]: any;
}

const ExerciseWithGlobalBluetooth: React.FC<ExerciseWithGlobalBluetoothProps> = ({ t, ...props }) => {
  const {
    isConnected,
    isCalibrated,
    isCalibrating,
    userWeight,
    addedWeights,
    connectionError,
    calibrationError,
    connect,
    startCalibration,
    ensureDataStreamActive,
  } = useBluetooth();

  const [exerciseReady, setExerciseReady] = useState(false);
  const [currentSensorData, setCurrentSensorData] = useState<number | null>(null);
  const [shouldStartWorkout, setShouldStartWorkout] = useState(false);
  const exerciseRef = useRef<any>(null);

  useEffect(() => {
    const ready = isConnected && isCalibrated;
    setExerciseReady(ready);
  }, [isConnected, isCalibrated]);

  useEffect(() => {
    if (exerciseReady) {
      setTimeout(() => {
        ensureDataStreamActive((data) => {
          // console.log('ExerciseWithGlobalBluetooth: Received raw sensor data:', data);
          
          const selectedSetup = (props.location as any)?.state?.selectedSetup || 'hangboard';
          
          // Process the data based on setup
          let processedData: number | string = data;
          
          // Detect "Out" state (user not on device)
          // Hangboard: Measured weight < 2kg means user is hanging (or device is empty)
          // Lift: Measured weight < 2kg means no weight is being lifted (or device is empty)
          const isOut = data < 2;

          if (selectedSetup === 'hangboard') {
            // Hangboard: Force pulled = Body weight - Measured weight
            if (isOut) {
              processedData = "Out";
            } else if (userWeight > 0) {
              processedData = userWeight - data;
            }
          } else if (selectedSetup === 'lift') {
            // Lift: Force pulled = Measured weight - Body weight
            if (isOut) {
              processedData = "Out";
            } else if (userWeight > 0) {
              processedData = data - userWeight;
            }
          } else if (selectedSetup === 'portable') {
            // Portable: Force pulled = Measured weight (no body weight calibration needed)
            processedData = data;
          }
          
          // Forward processed data to the Exercise component via state
          if (typeof processedData === 'number') {
            // Clamp negative values to 0 and round
            processedData = Math.max(0, processedData);
            processedData = Math.round(processedData * 10) / 10;
          }
          
          setCurrentSensorData(processedData as any);
        }).catch((error) => {
          console.error('ExerciseWithGlobalBluetooth: Failed to ensure data stream active:', error);
        });
        
        // Start the workout after data stream is established
        setTimeout(() => {
          setShouldStartWorkout(true);
        }, 50); // Minimized delay
      }, 50); // Minimized delay
    }
  }, [exerciseReady, userWeight, ensureDataStreamActive, (props.location as any)?.state?.selectedSetup]);

  // Simple ref assignment (no callback needed)
  const exerciseRefCallback = (ref: any) => {
    exerciseRef.current = ref;
  };

  const handleCalibrationComplete = (weight: number, addedWeights: number) => {
    // The data stream will be started by the useEffect when exerciseRef.current becomes available
  };

  // Show setup screen if not ready
  if (!exerciseReady) {
    return (
      <BluetoothSetupFlowWithContext
        onReady={() => {
          setExerciseReady(true);
          // Workout will be started when data stream is established and Exercise component is ready
        }}
        onCalibrationComplete={handleCalibrationComplete}
        showReadyStep={true}
        readyButtonText="Start Workout"
        readyDescription="Your device is connected and calibrated"
      />
    );
  }

  // Render the actual Exercise component when ready
  return <Exercise ref={exerciseRefCallback} {...props} sensorData={currentSensorData} shouldStartWorkout={shouldStartWorkout} />;
};

export default withTranslation('workout')(ExerciseWithGlobalBluetooth);
